import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Send, Eye, AlertTriangle, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  adminListEmailTemplates,
  adminPreviewEmailTemplate,
} from "@/lib/admin-email-test.functions";

export const Route = createFileRoute("/_authenticated/admin/email-test")({
  head: () => ({
    meta: [{ title: "Email test sender — Admin" }, { name: "robots", content: "noindex" }],
  }),
  component: EmailTestPage,
});

type TemplateRow = { name: string; displayName: string; hasPreviewData: boolean };

function EmailTestPage() {
  const list = useServerFn(adminListEmailTemplates);
  const preview = useServerFn(adminPreviewEmailTemplate);

  const [templates, setTemplates] = useState<TemplateRow[] | null>(null);
  const [selected, setSelected] = useState<string>("");
  const [recipient, setRecipient] = useState<string>("");
  const [html, setHtml] = useState<string>("");
  const [subject, setSubject] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sendResult, setSendResult] = useState<{ ok: boolean; message: string } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const rows = await list();
        setTemplates(rows);
        if (rows[0]) setSelected(rows[0].name);
      } catch (e) {
        setSendResult({ ok: false, message: String(e) });
      } finally {
        setLoading(false);
      }
    })();
  }, [list]);

  useEffect(() => {
    if (!selected) return;
    setHtml("");
    setSubject("");
    (async () => {
      try {
        const res = await preview({ data: { templateName: selected } });
        setHtml(res.html);
        setSubject(res.subject);
      } catch (e) {
        setSendResult({ ok: false, message: `Preview failed: ${e}` });
      }
    })();
  }, [selected, preview]);

  const validEmail = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient.trim()),
    [recipient],
  );

  async function onSend() {
    if (!selected || !validEmail) return;
    setBusy(true);
    setSendResult(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) throw new Error("not_authenticated");
      const res = await fetch("/lovable/email/transactional/send", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          templateName: selected,
          recipientEmail: recipient.trim(),
          idempotencyKey: `admin-test-${selected}-${Date.now()}`,
          templateData: templates?.find((t) => t.name === selected)?.hasPreviewData
            ? undefined
            : {},
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      setSendResult({
        ok: true,
        message: `Queued. Track delivery in /admin/emails (message id: ${json?.messageId ?? "n/a"}).`,
      });
    } catch (e) {
      setSendResult({ ok: false, message: String(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-10">
        <h1 className="font-display text-3xl font-bold">Email template tester</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Preview registered customer and helper templates with sample data, and send a real test
          email to yourself or a teammate to verify the new logo renders in Gmail, Outlook,
          Apple Mail, and Yahoo.
        </p>

        {loading ? (
          <div className="mt-10 flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading templates…
          </div>
        ) : (
          <div className="mt-8 grid gap-6 lg:grid-cols-[320px_1fr]">
            <aside className="space-y-4">
              <div>
                <Label htmlFor="tpl">Template</Label>
                <select
                  id="tpl"
                  className="mt-1 w-full rounded-md border border-border bg-card px-3 py-2 text-sm"
                  value={selected}
                  onChange={(e) => setSelected(e.target.value)}
                >
                  {templates?.map((t) => (
                    <option key={t.name} value={t.name}>
                      {t.displayName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="rcpt">Send test to</Label>
                <Input
                  id="rcpt"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  className="mt-1"
                />
                {recipient && !validEmail && (
                  <p className="mt-1 text-xs text-destructive">Enter a valid email address.</p>
                )}
              </div>

              <Button
                onClick={onSend}
                disabled={!selected || !validEmail || busy}
                className="w-full"
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                <span className="ml-2">Send test email</span>
              </Button>

              {sendResult && (
                <div
                  role="status"
                  className={`flex items-start gap-2 rounded-md border p-3 text-sm ${
                    sendResult.ok
                      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200"
                      : "border-destructive/30 bg-destructive/10 text-destructive"
                  }`}
                >
                  {sendResult.ok ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                  ) : (
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  )}
                  <span>{sendResult.message}</span>
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                Suppression list, RLS, and the queue still apply. Suppressed addresses are blocked
                before sending — clear them in /admin/emails if a test bounces.
              </p>
            </aside>

            <section>
              <div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
                <Eye className="h-4 w-4" />
                <span className="font-medium text-foreground">Subject:</span>
                <span>{subject || "—"}</span>
              </div>
              <div className="overflow-hidden rounded-2xl border border-border bg-white">
                {html ? (
                  <iframe
                    title="Email preview"
                    srcDoc={html}
                    sandbox=""
                    className="h-[720px] w-full"
                  />
                ) : (
                  <div className="grid h-[720px] place-items-center text-sm text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </div>
                )}
              </div>
            </section>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
